import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, TextField, Grid, DialogActions, Autocomplete, CircularProgress, ListItem, ListItemText, debounce, Typography, FormControl, InputLabel, MenuItem, Select, FormHelperText } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Stack } from '@mui/system';
export function CotacaoDialog({ CrmService, onClose, title, departamentos, ...props }) {
    // AutoComplete
    const [loadAutoComplete, setLoadAutoComplete] = useState(false);
    const [options, setOptions] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const noFetchRef = useRef([]); // contactos já associados, não pesquisar os mesmos
    const limit = 5; // Número de itens por página
    const validationSchema = yup.object({
        departamentoDestino: yup.number().required('Preenchimento Obrigatório'),
        pessoaResponsavel: yup
            .object()
            .shape({
            id: yup.number().required('Selecione um responsável')
        })
            .nullable()
            .required('Preenchimento Obrigatório'),
        notasComercial: yup.string().required('Preenchimento Obrigatório')
    });
    const formik = useFormik({
        initialValues: {
            departamentoDestino: null,
            pessoaResponsavel: null,
            notasComercial: null
        },
        validationSchema,
        validateOnChange: false,
        onSubmit: values => {
            onClose(values);
        }
    });
    const fetchOptions = useCallback(debounce(async (query, currentPage) => {
        if (!query || !hasMore)
            return;
        setLoadAutoComplete(true);
        try {
            const response = await CrmService.funcionarioAutocomplete(query, currentPage, limit, noFetchRef.current);
            if (response.length < limit) {
                setHasMore(false); // Não há mais dados
            }
            setOptions(prev => [...prev, ...response]); // Concatena os dados
        }
        catch (error) {
            console.error('Erro ao carregar opções:', error);
        }
        finally {
            setLoadAutoComplete(false);
        }
    }, 500), [hasMore] // Atualiza se `hasMore` mudar
    );
    const handleScroll = event => {
        const listboxNode = event.currentTarget;
        if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 1 && !loadAutoComplete && hasMore) {
            setPage(prev => prev + 1); // Incrementa a página
        }
    };
    const handleChange = (event) => {
        const { value } = event.target;
        formik.setFieldValue('departamentoDestino', value);
    };
    // AutoComplete
    useEffect(() => {
        if (inputValue) {
            fetchOptions(inputValue, page);
        }
    }, [inputValue, page]);
    return (<Dialog {...props} fullWidth maxWidth='lg' onClose={onClose}>
      <DialogTitle>{title || 'Detalhes'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6} lg={6}>
            <FormControl fullWidth size='small' error={formik.touched.departamentoDestino && Boolean(formik.errors.departamentoDestino)}>
              <InputLabel id='departamento-label' sx={{ mb: 1 }}>
                Departamento de destino interno
              </InputLabel>
              <Select labelId='departamento-label' id='departamento-select' size='small' value={formik.values.departamentoDestino || ''} onChange={handleChange} label='Departamento de destino interno'>
                {departamentos.map(d => (<MenuItem key={d.id} value={d.id}>
                    {d.designacao}
                  </MenuItem>))}
              </Select>
              <FormHelperText>{formik.touched.departamentoDestino && formik.errors.departamentoDestino}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <Autocomplete disablePortal={false} // lista não fica dentro da dialog
     options={options} getOptionLabel={option => (option.name ? `${option.name}` : '')} noOptionsText='Pesquise pelo nome do Colaborador' value={formik.values.pessoaResponsavel || null} // O valor completo da opção selecionada
     onChange={(event, newValue) => {
            formik.setFieldValue('pessoaResponsavel', newValue); // Armazena o objeto completo
        }} 
    //filterOptions={x => x} // Evita o filtro padrão
    onInputChange={(event, newInputValue, reason) => {
            if (reason !== 'reset') {
                setInputValue(newInputValue); // Só altera o input se for uma entrada do user
                setOptions([]);
                setPage(0);
                setHasMore(true);
            }
        }} ListboxProps={{
            onScroll: handleScroll // Adiciona evento de scroll
        }} renderOption={(props, option) => (<ListItem {...props} key={option.id} dense>
                  <ListItemText primary={option.name} secondary={<Stack direction='column'>
                        <Typography variant='caption'>Email: {option.email ?? 'Sem email'}</Typography>
                        <Typography variant='caption'>Nº Mecanográfico: {option.num ?? 'Não tem'}</Typography>
                      </Stack>}/>
                </ListItem>)} renderInput={params => (<TextField {...params} label='Colaborador' variant='outlined' size='small' fullWidth error={formik.errors.pessoaResponsavel && Boolean(formik.errors.pessoaResponsavel)} helperText={formik.touched.pessoaResponsavel?.name && formik.errors.pessoaResponsavel?.name} InputProps={{
                ...params.InputProps,
                endAdornment: (<>
                        {loadAutoComplete ? <CircularProgress color='inherit' size={20}/> : null}
                        {params.InputProps.endAdornment}
                      </>)
            }}/>)}/>
          </Grid>
          <Grid item xs={12} md={12} lg={12} sx={{ mt: 2 }}>
            <TextField fullWidth id='notasComercial' name='notasComercial' label='Descrição das solicitações a pedir ao departamento externo' color='info' size='small' multiline minRows={3} value={formik.values.notasComercial} onChange={formik.handleChange} error={formik.touched.notasComercial && Boolean(formik.errors.notasComercial)} helperText={formik.touched.notasComercial && formik.errors.notasComercial}/>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ mt: 2 }}>
        <Button onClick={() => {
            formik.handleSubmit();
        }} variant='contained'>
          Encaminhar Pedido
        </Button>
      </DialogActions>
    </Dialog>);
}
